import React, {
  createContext,
  useCallback,
  useContext,
  useId,
  useMemo,
  type ReactNode,
} from 'react';

import { EventBusEvent } from 'common/enums/EventBus';
import type { ArticleSortOption } from 'common/enums/SortOption';
import eventBus from 'common/event-bus/EventBus';
import { useBackNavigation } from 'common/hooks/back-navigation';
import { useCloseDrawer } from 'common/hooks/drawer';
import { useKeyboardClose } from 'common/hooks/keyboard';
import { useSubscription } from 'common/hooks/subscription';
import { trackFilterDrawerClose } from 'common/tracking/filters';
import type { DrawerSubscriptionState } from 'common/types/drawer';
import type { Filter, FiltersParams, SortParams } from 'common/types/SortAndFilter';
import { groupFilters, type FilterGroup } from 'common/utils/filterGroups';
import type { QueryParams } from 'common/utils/url';
import { useHandleFilters } from '../hooks/use-handle-filters';

export interface SortOption {
  localized: string;
  option: ArticleSortOption | string;
}

export type FilterChangeParams = QueryParams;

export interface FilterDrawerProps extends SortParams, FiltersParams {
  hideArticlesCount?: boolean;
}

interface DrawerSubscriptionData extends DrawerSubscriptionState {
  headline: string;
  excludeCategory?: boolean;
  showCategoriesAsFilter?: boolean;
  isSort?: boolean;
  initiallyExpanded?: string[];
}

interface FilterDrawerContextValue {
  isOpen: boolean;
  headline: string;
  closeDrawer: () => void;
  closeDrawerWithoutHistoryBack: () => void;
  ariaLabelId: string;
  showSortSection: boolean;
  isSortMode: boolean;
  hideArticlesCount: boolean;
  filters: Filter[];
  processedFilters: FilterGroup[];
  onFilterChange: (params: FilterChangeParams) => void;
  defaultExpandedSections: string[];
  sortOptions: SortOption[];
  currentOrder: ArticleSortOption;
  applyUrl: string;
  hasChanges: boolean;
}

const FilterDrawerContext = createContext<FilterDrawerContextValue | null>(null);

interface FilterDrawerProviderProps extends FilterDrawerProps {
  children: ReactNode;
}

export const FilterDrawerProvider = ({
  children,
  filters: initialFilters,
  sortOptions,
  order,
  hideArticlesCount = false,
}: FilterDrawerProviderProps) => {
  const ariaLabelId = useId();

  const [drawerData] = useSubscription<DrawerSubscriptionData>(
    EventBusEvent.ToggleCategoryFiltersDrawer
  );
  const isOpen = Boolean(drawerData?.open);

  const { onManualClose } = useBackNavigation({
    isOpen,
    toggleEvent: EventBusEvent.ToggleCategoryFiltersDrawer,
  });

  const drawerCloseCallback = useCallback(() => {
    trackFilterDrawerClose();
    onManualClose();
  }, [onManualClose]);

  const closeDrawer = useCloseDrawer({
    toggleEvent: EventBusEvent.ToggleCategoryFiltersDrawer,
    callback: drawerCloseCallback,
    isOpen,
  });

  const closeDrawerWithoutHistoryBack = useCallback(() => {
    eventBus.emit(EventBusEvent.ToggleCategoryFiltersDrawer, { open: false });
    trackFilterDrawerClose();
  }, []);

  useKeyboardClose(isOpen, closeDrawer);

  const { url, filters, haveFiltersChanged, defaultExpandedSections, onFilterChange } =
    useHandleFilters({
      filters: initialFilters,
      order,
      showCategoriesAsFilter: drawerData?.showCategoriesAsFilter,
      excludeCategory: drawerData?.excludeCategory,
      initiallyExpanded: drawerData?.initiallyExpanded,
    });

  const processedFilters = useMemo(() => groupFilters(filters), [filters]);

  const contextValue: FilterDrawerContextValue = useMemo(
    () => ({
      isOpen,
      headline: drawerData?.headline || '',
      closeDrawer,
      closeDrawerWithoutHistoryBack,
      ariaLabelId,
      showSortSection: !drawerData?.showCategoriesAsFilter,
      isSortMode: Boolean(drawerData?.isSort),
      hideArticlesCount,
      filters,
      processedFilters,
      onFilterChange,
      defaultExpandedSections,
      sortOptions: sortOptions || [],
      currentOrder: order,
      applyUrl: url,
      hasChanges: haveFiltersChanged,
    }),
    [
      isOpen,
      drawerData?.headline,
      drawerData?.showCategoriesAsFilter,
      drawerData?.isSort,
      closeDrawer,
      closeDrawerWithoutHistoryBack,
      ariaLabelId,
      hideArticlesCount,
      filters,
      processedFilters,
      onFilterChange,
      defaultExpandedSections,
      sortOptions,
      order,
      url,
      haveFiltersChanged,
    ]
  );

  return (
    <FilterDrawerContext.Provider value={contextValue}>{children}</FilterDrawerContext.Provider>
  );
};

export const useFilterDrawer = (): FilterDrawerContextValue => {
  const context = useContext(FilterDrawerContext);
  if (!context) {
    throw new Error('useFilterDrawer must be used within a FilterDrawerProvider');
  }

  return context;
};
